import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import UserContext from '../context/UserContext.js'
import useLearnerContext from '../hooks/useLearnerContext'
import {
  MY_SPEAKING_TASKS_QUERY, SPEAKING_TASK_QUERY, START_SPEAKING_TASK_MUTATION,
  WRITE_SPEAKING_PART_MUTATION, EDIT_SPEAKING_PART_MUTATION, DELETE_SPEAKING_TASK_MUTATION,
  RECORD_SPEAKING_RESULTS_MUTATION,
} from '../query/gqlQueries.js'
import languages from '../languages'
import voices from '../speech/voices'
import { speakTracked, stopSpeaking } from '../speech/speak'
import useSpeechToText from '../speech/useSpeechToText'
import { mergeTranscript, wordUnits } from '../speech/flowMatch'
import { sayLevel, recordSayLevel, hiddenAt, LEVEL_NAMES } from '../speech/sayLevel'

// Speaking homework, as in the iPhone app: paste your teacher's message ("talk
// for three minutes about…"), the Mac Studio reads it into topics; write what
// you want to say about each, it writes your part in the language; then
// practise saying it — from memory, or with every other word showing — with
// the words lighting up as you say them, as on the TV. Every answer counts
// towards the homework's percentage (lib/proficiency.js on the server).

export const card = { background: 'var(--frost-field)', border: '1px solid var(--frost-border)', borderRadius: 12, padding: '0.75rem 0.9rem' }
export const btn = { background: 'var(--frost-field)', color: 'var(--frost-text)', border: '1px solid var(--frost-border)', borderRadius: 9, padding: '0.45rem 0.9rem', cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'inherit' }
export const primary = { ...btn, background: 'var(--frost-action)', color: 'var(--frost-on-action)', border: 'none', fontWeight: 700 }
export const sectionLabel = { color: 'var(--frost-muted)', fontSize: '0.7rem', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '0.5rem' }
export const field = { width: '100%', boxSizing: 'border-box', background: '#0f0f13', color: 'var(--frost-text)', border: '1px solid var(--frost-border)', borderRadius: 9, padding: '0.55rem 0.7rem', fontSize: '0.9rem', fontFamily: 'inherit' }
export const muted = { color: 'var(--frost-muted)', fontSize: '0.82rem' }
export const errText = { color: 'var(--frost-wrong)', fontSize: '0.85rem' }
export const pctColor = (p) => (p >= 80 ? 'var(--frost-right)' : p >= 50 ? 'var(--frost-amber)' : 'var(--frost-wrong)')
export const fmtDue = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '')
export const POLL_MS = 4000
const schemaMissing = (e) => /Cannot query field|Unknown type/i.test(e?.message || '')

// A full-screen page over everything, like a lesson.
export const page = { position: 'fixed', inset: 0, zIndex: 2100, background: '#0d0d10', color: 'var(--frost-text)', overflowY: 'auto', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }
export const pageInner = { maxWidth: 760, margin: '0 auto', padding: '1.2rem 16px 4rem' }

const voiceFor = (lang) => voices[lang] || voices.eng
const cpsFor = (lang) => (['kor', 'jpn', 'cmn'].includes(lang) ? 4.5 : 13)
// Quote marks are never read aloud. Blanked rather than removed, so the
// voice's word positions still line up with the text on screen.
const sayable = (text) => String(text || '').replace(/["“”«»„‹›]/g, ' ')

export function Hear({ text, lang, voiceSpeed, title = 'Hear it' }) {
  return (
    <button style={{ ...btn, padding: '0.2rem 0.5rem', fontSize: '0.8rem' }} title={title}
      onClick={(e) => { e.stopPropagation(); speakTracked(sayable(text), voiceFor(lang), voiceSpeed, { cps: cpsFor(lang) }) }}>🔊</button>
  )
}

// Every language you're learning, for the add forms.
export function useMyLanguages() {
  const { username } = useContext(UserContext)
  const { learning } = useLearnerContext(username)
  return (learning || []).filter(l => l && l.code)
}

// ── The list on the homework page ───────────────────────────────────────────
export function SpeakingHomeworkList({ onOpen }) {
  const { data, error, refetch, startPolling, stopPolling } = useQuery(MY_SPEAKING_TASKS_QUERY, { variables: {}, fetchPolicy: 'network-only', errorPolicy: 'all' })
  const [del] = useMutation(DELETE_SPEAKING_TASK_MUTATION)
  const [adding, setAdding] = useState(false)
  const tasks = data?.mySpeakingTasks || []
  const busy = tasks.some(t => t.status === 'reading' || (t.topics || []).some(p => p.status === 'writing'))
  useEffect(() => { if (busy) { startPolling(POLL_MS); return () => stopPolling() } }, [busy, startPolling, stopPolling])

  if (schemaMissing(error)) return null

  const remove = async (t) => {
    if (!window.confirm(`Delete “${t.title || 'this speaking homework'}”? Your notes and lines go with it.`)) return
    try { await del({ variables: { id: t.id } }); refetch() } catch (e) { window.alert(e.message) }
  }

  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={sectionLabel}>Speaking homework</div>
        {!adding && <button style={{ ...btn, padding: '0.25rem 0.7rem', marginBottom: '0.5rem' }} onClick={() => setAdding(true)}>+ Add</button>}
      </div>
      {adding && <AddSpeaking onCancel={() => setAdding(false)} onAdded={() => { setAdding(false); refetch() }} />}
      {tasks.length === 0 && !adding && <div style={{ ...muted, padding: '0.2rem 0 0.4rem' }}>Paste a speaking task from your teacher — it's split into topics, you write your part, then practise saying it.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {tasks.map(t => {
          const lines = (t.topics || []).reduce((n, p) => n + (p.lines || []).length, 0)
          const pct = t.progress ?? 0
          return (
            <div key={t.id} style={{ ...card, display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap', cursor: t.status === 'ready' ? 'pointer' : 'default' }}
              onClick={() => t.status === 'ready' && onOpen(t.id)}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ color: 'var(--frost-text)', fontWeight: 600 }}>🗣️ {t.title || (t.status === 'reading' ? 'Reading your teacher’s message…' : 'Speaking homework')}</div>
                <div style={muted}>
                  {languages[t.lang] || t.lang}{t.teacher ? ` · from ${t.teacher}` : ''}{t.due ? ` · due ${fmtDue(t.due)}` : ''}
                  {t.status === 'ready' ? ` · ${(t.topics || []).length} topics${lines ? ` · ${lines} lines` : ''}` : ''}
                </div>
                {t.status === 'failed' && <div style={errText}>{t.error || 'The message could not be read.'}</div>}
              </div>
              {t.status === 'ready' && lines > 0 && <span style={{ color: pctColor(pct), fontWeight: 700, fontSize: '0.9rem' }}>{pct}% known</span>}
              {t.status === 'reading' && <span style={muted}>Reading…</span>}
              {t.status === 'ready' && <button style={primary} onClick={(e) => { e.stopPropagation(); onOpen(t.id) }}>Open</button>}
              <button style={{ ...btn, color: 'var(--frost-muted)' }} title="Delete" onClick={(e) => { e.stopPropagation(); remove(t) }}>✕</button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function AddSpeaking({ onCancel, onAdded }) {
  const langs = useMyLanguages()
  const [lang, setLang] = useState('')
  const [text, setText] = useState('')
  const [teacher, setTeacher] = useState('')
  const [due, setDue] = useState('')
  const [msg, setMsg] = useState('')
  const [start, { loading }] = useMutation(START_SPEAKING_TASK_MUTATION)
  const chosen = lang || langs[0]?.code || ''

  const add = async () => {
    setMsg('')
    if (!chosen) { setMsg('Add the language you’re learning to your profile first.'); return }
    if (!text.trim()) { setMsg('Paste your teacher’s message.'); return }
    try {
      await start({ variables: { lang: chosen, text: text.trim(), teacher: teacher.trim() || null, due: due || null } })
      onAdded()
    } catch (e) { setMsg(e.message) }
  }

  return (
    <div style={{ ...card, marginBottom: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
      <select style={field} value={chosen} onChange={e => setLang(e.target.value)}>
        {langs.map(l => <option key={l.code} value={l.code}>{l.name || languages[l.code] || l.code}</option>)}
      </select>
      <textarea style={{ ...field, minHeight: 120 }} placeholder="Paste your teacher’s message — the task, the topics, the questions…" value={text} onChange={e => setText(e.target.value)} />
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <input style={{ ...field, flex: 2, minWidth: 160 }} placeholder="Teacher (optional)" value={teacher} onChange={e => setTeacher(e.target.value)} />
        <label style={{ ...muted, flex: 1, minWidth: 150, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>Due
          <input type="date" style={{ ...field, colorScheme: 'inherit' }} value={due} onChange={e => setDue(e.target.value)} />
        </label>
      </div>
      {msg && <div style={errText}>{msg}</div>}
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button style={btn} onClick={onCancel}>Cancel</button>
        <button style={primary} disabled={loading} onClick={add}>{loading ? 'Adding…' : 'Add'}</button>
      </div>
      <div style={muted}>The Mac Studio reads it into topics — that takes a minute or so. You can close this meanwhile.</div>
    </div>
  )
}

// ── One speaking homework ───────────────────────────────────────────────────
export function SpeakingTaskView({ id, voiceSpeed, onClose }) {
  const { data, error, loading, refetch, startPolling, stopPolling } = useQuery(SPEAKING_TASK_QUERY, { variables: { id }, fetchPolicy: 'network-only', errorPolicy: 'all' })
  const [del] = useMutation(DELETE_SPEAKING_TASK_MUTATION)
  const [practice, setPractice] = useState(null) // 'memory' | 'gaps'
  const [talking, setTalking] = useState(null)   // index of the line being read in "the whole talk"
  const talkRef = useRef(null)
  const task = data?.speakingTask
  const busy = task && (task.status === 'reading' || task.topics.some(p => p.status === 'writing'))
  useEffect(() => { if (busy) { startPolling(POLL_MS); return () => stopPolling() } }, [busy, startPolling, stopPolling])
  useEffect(() => () => { if (talkRef.current) talkRef.current.stop = true; stopSpeaking() }, [])

  const lines = useMemo(() => (task?.topics || []).flatMap(t => (t.lines || []).map(l => ({ ...l, topic: t.title }))).filter(l => l.said), [task])

  const hearAll = async () => {
    if (talkRef.current) { talkRef.current.stop = true; talkRef.current.cancel?.(); talkRef.current = null; setTalking(null); return }
    const run = { stop: false }
    talkRef.current = run
    for (let i = 0; i < lines.length && !run.stop; i++) {
      setTalking(i)
      const s = speakTracked(sayable(lines[i].said), voiceFor(task.lang), voiceSpeed, { cps: cpsFor(task.lang) })
      run.cancel = s.cancel
      await s.promise
      if (!run.stop) await new Promise(r => setTimeout(r, 350))
    }
    if (talkRef.current === run) { talkRef.current = null; setTalking(null) }
  }

  const remove = async () => {
    if (!window.confirm('Delete this speaking homework? Your notes and lines go with it.')) return
    try { await del({ variables: { id } }); onClose() } catch (e) { window.alert(e.message) }
  }

  if (practice && task) {
    return <SpeakingPractice task={task} mode={practice} voiceSpeed={voiceSpeed} onExit={() => { setPractice(null); refetch() }} />
  }

  let lineNo = -1
  return (
    <div style={page}>
      <div style={pageInner}>
        <button style={btn} onClick={onClose}>← Homework</button>
        {loading && !task && <div style={{ ...muted, marginTop: '2rem' }}>Loading…</div>}
        {error && !task && <div style={{ ...errText, marginTop: '2rem' }}>{error.message}</div>}
        {task && (
          <>
            <div style={{ marginTop: '1.2rem' }}>
              <div style={sectionLabel}>Speaking homework · {languages[task.lang] || task.lang}</div>
              <h2 style={{ margin: '0 0 0.2rem', fontSize: '1.6rem' }}>{task.title || 'Speaking homework'}</h2>
              {task.meaning && <div style={{ color: 'var(--frost-muted)' }}>{task.meaning}</div>}
              <div style={{ ...muted, marginTop: '0.35rem' }}>
                {task.teacher ? `from ${task.teacher}` : ''}{task.due ? `${task.teacher ? ' · ' : ''}due ${fmtDue(task.due)}` : ''}
                {task.seconds ? `${task.teacher || task.due ? ' · ' : ''}speak for about ${Math.max(1, Math.round(task.seconds / 60))} min` : ''}
              </div>
            </div>

            {task.status === 'reading' && <div style={{ ...card, marginTop: '1rem' }}>Reading your teacher’s message into topics… this takes a minute or so.</div>}
            {task.status === 'failed' && <div style={{ ...card, marginTop: '1rem', ...errText }}>{task.error || 'The message could not be read.'}</div>}

            {lines.length > 0 && (
              <div style={{ ...card, marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={{ fontWeight: 700, color: pctColor(task.progress ?? 0), fontSize: '1.3rem' }}>{task.progress ?? 0}% known</div>
                  <div style={muted}>{lines.length} lines · from memory counts in full, with words showing half</div>
                </div>
                <button style={btn} onClick={hearAll}>{talking !== null ? '■ Stop' : '🔊 Hear the whole talk'}</button>
                <button style={btn} onClick={() => setPractice('gaps')}>Fill the gaps</button>
                <button style={primary} onClick={() => setPractice('memory')}>From memory</button>
              </div>
            )}

            {task.topics.map(t => (
              <Topic key={t.index} task={task} topic={t} voiceSpeed={voiceSpeed} talkingLine={(() => {
                const first = lineNo + 1
                lineNo += (t.lines || []).filter(l => l.said).length
                return talking !== null && talking >= first && talking <= lineNo ? talking - first : null
              })()} />
            ))}

            {task.source && (
              <details style={{ marginTop: '1.4rem' }}>
                <summary style={{ ...muted, cursor: 'pointer' }}>Your teacher’s message</summary>
                <div style={{ ...card, marginTop: '0.5rem', whiteSpace: 'pre-wrap', color: 'var(--frost-text)', fontSize: '0.9rem' }}>{task.source}</div>
              </details>
            )}
            <div style={{ marginTop: '1.6rem' }}><button style={{ ...btn, color: 'var(--frost-wrong)' }} onClick={remove}>Delete this homework</button></div>
          </>
        )}
      </div>
    </div>
  )
}

function Topic({ task, topic, voiceSpeed, talkingLine }) {
  const [notes, setNotes] = useState(topic.notes || '')
  const [editing, setEditing] = useState(null) // text being edited
  const [msg, setMsg] = useState('')
  const [write, { loading: writing }] = useMutation(WRITE_SPEAKING_PART_MUTATION)
  const [edit, { loading: saving }] = useMutation(EDIT_SPEAKING_PART_MUTATION)
  const lines = (topic.lines || []).filter(l => l.said)
  useEffect(() => { setNotes(n => (n ? n : topic.notes || '')) }, [topic.notes])

  const writePart = async () => {
    setMsg('')
    if (!notes.trim()) { setMsg('Write a few notes first — in any language.'); return }
    try { await write({ variables: { id: task.id, topic: topic.index, notes: notes.trim() } }) } catch (e) { setMsg(e.message) }
  }
  const saveEdit = async () => {
    setMsg('')
    try { await edit({ variables: { id: task.id, topic: topic.index, text: editing } }); setEditing(null) } catch (e) { setMsg(e.message) }
  }

  return (
    <div style={{ ...card, marginTop: '1rem' }}>
      <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'baseline' }}>
        <span style={{ color: 'var(--frost-right)', fontWeight: 700 }}>{topic.index + 1}</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: '1.08rem' }}>{topic.title}</div>
          {topic.meaning && <div style={muted}>{topic.meaning}</div>}
        </div>
      </div>
      {topic.questions.length > 0 && (
        <ul style={{ margin: '0.6rem 0 0', paddingLeft: '1.2rem' }}>
          {topic.questions.map((q, i) => (
            <li key={i} style={{ marginBottom: '0.3rem' }}>
              <span>{q.text}</span> <Hear text={q.text} lang={task.lang} voiceSpeed={voiceSpeed} />
              {q.meaning && <div style={muted}>{q.meaning}</div>}
            </li>
          ))}
        </ul>
      )}

      <div style={{ marginTop: '0.8rem' }}>
        <div style={{ ...sectionLabel, marginBottom: '0.3rem' }}>Your notes</div>
        <textarea style={{ ...field, minHeight: 70 }} placeholder="What you want to say about this — in any language, as notes." value={notes} onChange={e => setNotes(e.target.value)} />
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.45rem', flexWrap: 'wrap' }}>
          <button style={lines.length ? btn : primary} disabled={writing || topic.status === 'writing'} onClick={writePart}>
            {topic.status === 'writing' ? 'Writing your part…' : lines.length ? 'Write it again' : 'Write my part'}
          </button>
          {topic.status === 'writing' && <span style={muted}>on the Mac Studio — a minute or so</span>}
        </div>
        {topic.status === 'failed' && <div style={{ ...errText, marginTop: '0.4rem' }}>{topic.error || 'Your part could not be written.'}</div>}
        {msg && <div style={{ ...errText, marginTop: '0.4rem' }}>{msg}</div>}
      </div>

      {lines.length > 0 && editing === null && (
        <div style={{ marginTop: '0.9rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ ...sectionLabel, marginBottom: '0.3rem' }}>Your part</div>
            <button style={{ ...btn, padding: '0.2rem 0.6rem', fontSize: '0.8rem' }} onClick={() => setEditing(lines.map(l => l.said).join('\n'))}>Edit</button>
          </div>
          {lines.map((l, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', padding: '0.35rem 0', borderTop: i ? '1px solid var(--frost-border)' : 'none' }}>
              <Hear text={l.said} lang={task.lang} voiceSpeed={voiceSpeed} />
              <div>
                <div style={{ fontSize: '1.02rem', color: talkingLine === i ? '#c7bdff' : 'var(--frost-text)', transition: 'color 0.25s' }}>{l.said}</div>
                {l.meaning && <div style={muted}>{l.meaning}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
      {editing !== null && (
        <div style={{ marginTop: '0.9rem' }}>
          <div style={{ ...sectionLabel, marginBottom: '0.3rem' }}>Edit your part</div>
          <textarea style={{ ...field, minHeight: 140 }} value={editing} onChange={e => setEditing(e.target.value)} />
          <div style={{ ...muted, margin: '0.3rem 0' }}>Changed sentences get new meanings, and start again at not known.</div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button style={btn} onClick={() => setEditing(null)}>Cancel</button>
            <button style={primary} disabled={saving} onClick={saveEdit}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Practice, as on the TV ──────────────────────────────────────────────────

// How much of a missing word a hint shows: a quarter, then half.
const hintPrefix = (word, level) => {
  const n = Array.from(word).length
  if (level <= 0 || n <= 1) return 0
  return Math.min(n - 1, Math.max(1, Math.ceil(n * (level >= 2 ? 0.5 : 0.25))))
}

// Every other word hidden, from a random start: at least one, never all.
// From memory hides every word; Fill the gaps as many as this sentence is ready for.
function hiddenFor(units, allHidden, level) {
  const real = units.map((u, i) => (u.to > u.from ? i : -1)).filter(i => i >= 0)
  return allHidden ? new Set(real) : hiddenAt(real, level)
}

const GREEN = 'var(--frost-right)', AMBER = 'var(--frost-amber)', CORAL = 'var(--frost-wrong)', SAYING = '#ccbdff'
const FADE_OUT = 300, FADE_IN = 450

// Also practice homework's (StudyHomework.js): `lines` instead of the talk's
// topics, and `onRecord(line, how, correct)` → a promise of the new % known.
export function SpeakingPractice({ task, mode, voiceSpeed, onExit, lines, onRecord, progress }) {
  const lang = task.lang
  const allHidden = mode === 'memory'
  const round = useMemo(() => {
    const all = lines || task.topics.flatMap(t => (t.lines || []).filter(l => l.said).map(l => ({ ...l, topic: t.title })))
    return allHidden ? all.filter(l => l.meaning) : all
  }, [task, allHidden, lines])

  const [index, setIndex] = useState(0)
  const [right, setRight] = useState(0)
  const [visible, setVisible] = useState(true)
  const [hidden, setHidden] = useState(new Set())
  const [states, setStates] = useState([])    // per token: 'pending' | 'hit'
  const [revealed, setRevealed] = useState(false)
  const [hint, setHint] = useState(0)
  const [saidRight, setSaidRight] = useState(false)
  const [nearly, setNearly] = useState(false)
  const [practisedAll, setPractisedAll] = useState(false)
  const [saying, setSaying] = useState(null)  // the unit being read aloud
  const [pct, setPct] = useState(progress ?? task.progress ?? 0)
  const [micOn, setMicOn] = useState(true)
  const [level, setLevel] = useState(1)
  const [record] = useMutation(RECORD_SPEAKING_RESULTS_MUTATION)

  const current = round[index] || null
  const shape = useMemo(() => (current ? wordUnits(current.said, lang) : { units: [], tokens: [], spaced: true }), [current, lang])

  // Everything the speech handlers need, current, without re-binding them.
  const live = useRef({})
  live.current = { index, states, revealed, saidRight, practisedAll, nearly, hint, shape, current }
  const voiceRef = useRef({ speaking: false, quietUntil: 0, cancel: null })
  const timers = useRef([])
  const advanceAfterVoice = useRef(false)
  const answered = useRef(false) // one answer per line, however many clicks
  const later = (fn, ms) => { timers.current.push(setTimeout(fn, ms)) }
  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = [] }
  useEffect(() => () => { clearTimers(); voiceRef.current.cancel?.(); stopSpeaking() }, [])

  const say = (onEnd) => {
    const c = live.current.current
    if (!c) return
    voiceRef.current.cancel?.()
    const units = live.current.shape.units
    voiceRef.current.speaking = true
    const s = speakTracked(sayable(c.said), voiceFor(lang), voiceSpeed, {
      cps: cpsFor(lang),
      onBoundary: ({ charIndex }) => {
        let u = null
        for (let i = 0; i < units.length; i++) if (units[i].start <= charIndex) u = i
        setSaying(u)
      },
      onEnd: () => {
        setSaying(null)
        voiceRef.current.speaking = false
        // The mic hears the speakers too: ignore it a moment longer.
        voiceRef.current.quietUntil = Date.now() + 500
        if (onEnd) onEnd()
      },
    })
    voiceRef.current.cancel = s.cancel
  }

  const prepare = (i) => {
    clearTimers()
    advanceAfterVoice.current = false
    answered.current = false
    const line = round[i]
    const sh = line ? wordUnits(line.said, lang) : { units: [], tokens: [] }
    const lv = line ? sayLevel(line.said, lang) : 1
    setLevel(lv)
    setHidden(hiddenFor(sh.units, allHidden, lv))
    setStates(sh.tokens.map(() => 'pending'))
    setRevealed(false); setHint(0); setSaidRight(false); setNearly(false); setPractisedAll(false); setSaying(null)
  }
  useEffect(() => { prepare(0) }, [])

  const answer = (correct) => {
    const { current: c, index: at, hint: h, saidRight: all, nearly: near, revealed: shown } = live.current
    if (!c || answered.current) return
    answered.current = true
    // How many words this sentence hides next time.
    if (!allHidden) recordSayLevel(!correct ? 'missed' : h === 0 && (all || (near && !shown)) ? 'clean' : 'helped', c.said, lang)
    clearTimers()
    voiceRef.current.cancel?.()
    // From memory with a hint is no longer from memory: it counts as gaps.
    const how = allHidden && h === 0 ? 'memory' : 'gaps'
    const saved = onRecord
      ? onRecord(c, how, correct)
      : record({ variables: { taskId: task.id, results: [{ key: c.said.trim(), mode: how, correct }] } }).then(r => r.data?.recordSpeakingResults?.pct)
    saved.then(p => { if (typeof p === 'number') setPct(p) }).catch(() => {})
    if (correct) setRight(n => n + 1)
    // The line fades out, then the next fades in.
    setVisible(false)
    later(() => { setIndex(at + 1); prepare(at + 1); later(() => setVisible(true), 30) }, FADE_OUT)
  }

  const advanceSoon = () => {
    advanceAfterVoice.current = false
    const at = live.current.index
    later(() => { if (live.current.index === at && live.current.saidRight) answer(true) }, 3000)
  }

  const reveal = () => {
    if (live.current.revealed) return
    setRevealed(true)
    live.current.revealed = true
    say(() => { if (advanceAfterVoice.current) advanceSoon() })
  }

  // What the microphone heard: light up the words. All of them — the answer
  // shows, is read, and the next comes three seconds after the voice. 90% —
  // say so and keep listening; moving on is the learner's call. After the
  // answer shows, read it out until it's all green.
  const heard = (text) => {
    const v = voiceRef.current
    if (v.speaking || Date.now() < v.quietUntil) return
    const s = live.current
    if (!s.current || !s.states.length || s.saidRight || s.practisedAll) return
    const next = mergeTranscript(s.states, text, s.shape.tokens, lang, false)
    const hits = next.filter(x => x === 'hit').length
    live.current.states = next
    setStates(next)
    if (s.revealed) {
      if (hits >= next.length) {
        setPractisedAll(true)
        live.current.practisedAll = true
        const at = s.index, recalled = s.nearly
        later(() => { if (live.current.index === at) answer(recalled) }, 3000)
      }
      return
    }
    if (hits >= next.length) {
      setSaidRight(true)
      live.current.saidRight = true
      advanceAfterVoice.current = true
      reveal()
    } else if (hits >= next.length * 0.9) {
      setNearly(true)
      live.current.nearly = true
    }
  }

  const stt = useSpeechToText(lang, { onFinal: heard })
  const blocked = /blocked|not-allowed|service-not-allowed|audio-capture/i.test(stt.error || '')
  const wantMic = micOn && !!current && stt.supported && !blocked
  // Recognition stops by itself after a pause; keep it going while practising.
  useEffect(() => {
    if (!wantMic) { if (stt.listening) stt.stop(); return }
    if (!stt.listening) { const t = setTimeout(() => stt.start(), 250); return () => clearTimeout(t) }
  }, [wantMic, stt.listening])
  // Words go green while you're still speaking, not only after a pause.
  useEffect(() => { if (stt.interim) heard(stt.interim) }, [stt.interim])

  const restart = () => { setIndex(0); setRight(0); prepare(0); setVisible(true) }

  const practising = revealed && !saidRight && !practisedAll
  const anyHit = states.includes('hit')
  const heardUnit = (u) => u.to > u.from && states.slice(u.from, u.to).every(x => x === 'hit')

  const word = (u, i) => {
    const gap = hidden.has(i)
    const isHeard = heardUnit(u)
    const sayableUnit = u.to > u.from
    let el
    if (!sayableUnit) el = <span style={{ color: 'rgba(255,255,255,0.7)' }}>{u.raw}</span>
    else if (i === saying && (!gap || revealed || isHeard)) el = <span style={{ color: SAYING, textDecoration: 'underline' }}>{u.raw}</span>
    else if (gap && !revealed && !isHeard) {
      const chars = Array.from(u.raw)
      const shown = hintPrefix(u.raw, hint)
      el = <span><span style={{ color: 'rgba(255,255,255,0.85)' }}>{chars.slice(0, shown).join('')}</span><span style={{ color: 'rgba(255,255,255,0.4)' }}>{'＿'.repeat(Math.max(2, Math.min(chars.length - shown, 6)))}</span></span>
    } else if (isHeard) el = <span style={{ color: GREEN }}>{u.raw}</span>
    else if (revealed && anyHit) el = <span style={{ color: CORAL, textDecoration: 'underline' }}>{u.raw}</span>
    else el = <span style={{ color: gap ? AMBER : '#fff' }}>{u.raw}</span>
    return <React.Fragment key={i}>{i > 0 && shape.spaced ? ' ' : ''}{el}</React.Fragment>
  }

  const exit = () => { clearTimers(); voiceRef.current.cancel?.(); stt.stop(); onExit() }

  return (
    <div style={page}>
      <div style={{ ...pageInner, minHeight: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <button style={btn} onClick={exit}>← Back</button>
          <div style={{ flex: 1, textAlign: 'center', ...muted }}>{allHidden ? 'From memory' : 'Fill the gaps'}{current ? ` · ${index + 1} / ${round.length}` : ''}{current && !allHidden ? ` · words hidden: ${LEVEL_NAMES[level].toLowerCase()}` : ''}</div>
          <span style={{ color: pctColor(pct), fontWeight: 700 }}>{pct}%</span>
        </div>

        {round.length === 0 && <div style={{ ...muted, marginTop: '3rem', textAlign: 'center' }}>{allHidden ? 'No lines with a meaning to practise from yet.' : 'Write your part first.'}</div>}

        {current && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', gap: '1.6rem', padding: '2rem 0' }}>
            <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'scale(0.98)', transition: visible ? `opacity ${FADE_IN}ms ease-out, transform ${FADE_IN}ms ease-out` : `opacity ${FADE_OUT}ms ease-in`, display: 'flex', flexDirection: 'column', gap: '1.4rem', alignItems: 'center' }}>
              {current.topic && <div style={sectionLabel}>{current.topic}</div>}
              {current.meaning && <div style={{ fontSize: '1.35rem', color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>{current.meaning}</div>}
              <div style={{ fontSize: 'clamp(1.6rem, 4.5vw, 2.6rem)', fontWeight: 600, lineHeight: 1.35 }}>{shape.units.map(word)}</div>
            </div>

            <div style={{ minHeight: '1.6rem', fontWeight: 600 }}>
              {saidRight ? <span style={{ color: GREEN }}>✓ All of it!</span>
                : practisedAll ? <span style={{ color: GREEN }}>✓ All green — well said.</span>
                  : practising ? <span style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 400 }}>{stt.listening ? '🎙 Now read it out — get every word green.' : 'Now read it out loud.'}</span>
                    : nearly && !revealed ? <span style={{ color: 'rgba(116,230,140,0.85)' }}>Nearly all of it — say the rest, or move on.</span>
                      : <span style={{ color: 'rgba(255,255,255,0.6)', fontWeight: 400 }}>{stt.listening ? '🎙 Say it out loud — the words light up as you’re heard.' : 'Say the whole line out loud, then check.'}</span>}
            </div>

            {revealed ? (
              <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                <button style={btn} onClick={() => say()}>🔊 Hear it again</button>
                <button style={btn} onClick={() => answer(false)}>Not yet</button>
                <button style={primary} onClick={() => answer(true)}>Got it ✓</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                {hint < 2 && hidden.size > 0 && <button style={btn} onClick={() => setHint(h => Math.min(2, h + 1))}>💡 {hint === 0 ? 'Hint' : 'Another hint'}</button>}
                <button style={nearly ? btn : primary} onClick={reveal}>Show the answer</button>
                {nearly && <button style={primary} onClick={() => answer(true)}>Got it ✓</button>}
              </div>
            )}

            <div style={{ ...muted, display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
              {!stt.supported ? <span>This browser can’t listen — say it aloud and check yourself (Chrome, Edge and Safari can).</span>
                : blocked ? <span style={errText}>Microphone blocked — allow it for this site to have your words light up.</span>
                  : <button style={{ ...btn, padding: '0.2rem 0.6rem', fontSize: '0.8rem' }} onClick={() => setMicOn(m => !m)}>{micOn ? '🎙 Mic on' : '🎙 Mic off'}</button>}
            </div>
          </div>
        )}

        {!current && round.length > 0 && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '1rem' }}>
            <div style={{ fontSize: '2.6rem', fontWeight: 800 }}>{right} of {round.length} right</div>
            <div style={{ color: pctColor(pct), fontWeight: 700 }}>{pct}% known</div>
            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <button style={btn} onClick={exit}>Done</button>
              <button style={primary} onClick={restart}>Go again</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
